import { defineStore } from "pinia";

import { MerchantApiError } from "../features/authentication/errors.js";
import type {
  BootstrapDependencies,
  BootstrapStatus,
  MerchantBootstrapResponse,
  MerchantBootstrapState,
} from "../features/authentication/models.js";
import type { MerchantSessionContext } from "../types.js";

export const useMerchantSessionStore = defineStore("merchant-session", {
  state: (): MerchantBootstrapState & { dependencies?: BootstrapDependencies } => ({
    status: "idle",
    capabilities: {},
    unreadNotificationCount: 0,
    offline: readInitialOfflineState(),
  }),
  getters: {
    hasResolvedSession: (state): boolean => state.context !== undefined,
    sessionContext: (state): MerchantSessionContext | undefined => state.context,
  },
  actions: {
    configure(dependencies: BootstrapDependencies): void {
      this.dependencies = dependencies;
    },
    async bootstrap(): Promise<void> {
      await this.bootstrapWithConfiguredDependencies();
    },
    markOnlineStatus(online: boolean): void {
      this.offline = !online;
      if (!online && this.status !== "ready") {
        this.status = "offline";
      }
    },
    setUnreadNotificationCount(count: number): void {
      this.unreadNotificationCount = Math.max(0, Math.floor(count));
    },
    async bootstrapWithConfiguredDependencies(): Promise<void> {
      this.startBootstrap();
      try {
        this.applyBootstrapResponse(await this.loadBootstrapResponse());
      } catch (error: unknown) {
        this.applyBootstrapError(error);
      }
    },
    startBootstrap(): void {
      this.status = this.offline ? "offline" : "loading";
      delete this.errorMessage;
      delete this.errorCode;
      delete this.requestId;
    },
    async loadBootstrapResponse(): Promise<MerchantBootstrapResponse> {
      if (this.dependencies === undefined) {
        throw new Error("Merchant session store was not configured before bootstrap.");
      }

      return this.dependencies.loadBootstrap();
    },
    applyBootstrapResponse(response: MerchantBootstrapResponse): void {
      this.context = {
        user: response.user,
        store: response.store,
        navigation: response.navigation,
      };
      this.onboarding = response.onboarding;
      this.integrationHealth = response.integrationHealth;
      this.capabilities = response.capabilities;
      this.unreadNotificationCount = response.unreadNotificationCount;
      this.status = statusForBootstrapResponse(response);
      this.dependencies?.track("merchant_bootstrap_result", {
        result: this.status,
        role: response.user.role,
        userStatus: response.user.status,
        storeStatus: response.store.status,
      });
    },
    applyBootstrapError(error: unknown): void {
      this.status = statusForBootstrapError(error);
      this.errorMessage = messageForBootstrapError(error);
      this.errorCode = error instanceof MerchantApiError ? error.code : "BOOTSTRAP_FAILED";
      if (error instanceof MerchantApiError) {
        this.requestId = error.requestId;
      } else {
        delete this.requestId;
      }
      this.dependencies?.track("merchant_bootstrap_result", {
        result: this.status,
        errorCode: this.errorCode,
      });
    },
  },
});

function readInitialOfflineState(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

function statusForBootstrapResponse(response: MerchantBootstrapResponse): BootstrapStatus {
  if (
    response.impersonated === true &&
    ["REAUTHORIZATION_REQUIRED", "UNAVAILABLE"].includes(response.integrationHealth.status)
  ) {
    return "degraded";
  }

  if (["REAUTHORIZATION_REQUIRED", "UNAVAILABLE"].includes(response.integrationHealth.status)) {
    return "reauthorization-required";
  }

  if (response.user.status === "PENDING" || response.user.status === "PENDING_APPROVAL")
    return "access-pending";
  if (response.user.status === "SUSPENDED") return "suspended";
  if (
    response.integrationHealth.status === "DEGRADED" &&
    response.integrationHealth.missingScopes.length > 0
  ) {
    return "degraded";
  }

  return "ready";
}

function statusForBootstrapError(error: unknown): BootstrapStatus {
  if (error instanceof MerchantApiError && error.code === "PLATFORM_REAUTHORIZATION_REQUIRED")
    return "reauthorization-required";
  if (error instanceof MerchantApiError && error.code === "ACCESS_PENDING") return "access-pending";
  if (typeof navigator !== "undefined" && !navigator.onLine) return "offline";
  return "error";
}

function messageForBootstrapError(error: unknown): string {
  if (error instanceof MerchantApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Price Rescue could not start. Try reopening the app from your commerce platform.";
}
